import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { CubiqResolver } from "../CubiqResolver";
import { BaseEncoder } from "./BaseEncoder";
import { BaseObjectEncoder } from "./BaseObjectEncoder";
import { Encodable } from "../model/Encodable";
import { ConcreteEncodable } from "../model/impl/ConcreteEncodable";
import { Message } from "../model/impl/Message";

// import ourselves
import * as CubiqTS from "../index";

export class DefaultBaseObjectEncoderImpl implements BaseObjectEncoder {
  protected static resolver: CubiqResolver = new CubiqResolver();

  // format() is less tolerant than parsing constructor
  // please see https://momentjs.com/docs/#/parsing/ (noting case)
  protected static serverDateToken: string = "YYYY-MM-DD HH:mm:ss.SSS";

  public constructor() {}

  public decode(encodable: ConcreteEncodable, streamin: CubiqTS.ByteBuffer) {
    if (encodable.isInnerEncoding() == false) encodable.encodableType = this.readString(streamin);
    encodable.majorRevision = this.readInt(streamin);
    encodable.minorRevision = this.readInt(streamin);
    encodable.extendedInfo = this.readMap(streamin);
  }

  public encode(encodable: ConcreteEncodable, streamout: CubiqTS.ByteBuffer) {
    this.writeString(encodable.encodableType, streamout);
    this.writeInt(encodable.majorRevision, streamout);
    this.writeInt(encodable.minorRevision, streamout);
    this.writeMap(encodable.extendedInfo, streamout);
  }

  public readBoolean(streamin: CubiqTS.ByteBuffer): boolean {
    return "Y" === this.readString(streamin);
  }

  public readDouble(streamin: CubiqTS.ByteBuffer): CubiqTS.BigNumber {
    let valStr = this.readString(streamin);
    let val = new CubiqTS.BigNumber(valStr);
    return val;
  }

  public readBigDecimal(streamin: CubiqTS.ByteBuffer): CubiqTS.BigNumber {
    let valStr = this.readString(streamin);
    if (valStr != null) {
      this.readInt(streamin);
    }
    let val = new CubiqTS.BigNumber(valStr);
    return val;
  }

  public readLongFromString(streamin: CubiqTS.ByteBuffer): CubiqTS.Long {
    let valStr = this.readString(streamin);
    let val = CubiqTS.Long.fromString(valStr);
    return val;
  }

  public readNumberFromString(streamin: CubiqTS.ByteBuffer): number {
    let valStr = this.readString(streamin);
    let val = parseFloat(valStr);
    return val;
  }

  public readByte(streamin: CubiqTS.ByteBuffer): string {
    let b: string = streamin.readString(1);
    return b;
  }
  public readByteBuffer(streamin: CubiqTS.ByteBuffer): CubiqTS.ByteBuffer {
    let len = this.readInt(streamin);
    if (len === 0) return null;
    let bb: CubiqTS.ByteBuffer = CubiqTS.ByteBuffer.allocate(len);
    streamin.copyTo(bb, 0, 0, len);
    //for(let i=0;i<len;++i)
    //bb.writeByte(streamin.readByte());
    bb.flip();
    return bb;
  }
  public readString(streamin: CubiqTS.ByteBuffer): string {
    return streamin.readIString();
  }

  public readInt(streamin: CubiqTS.ByteBuffer): number {
    return streamin.readInt();
  }

  public readLong(streamin: CubiqTS.ByteBuffer): CubiqTS.Long {
    return streamin.readInt64();
  }

  public readMap(streamin: CubiqTS.ByteBuffer): Map<string, string> {
    let mapSize: number = this.readInt(streamin);
    let map: Map<string, string> = new Map<string, string>();

    for (let i = 0; i < mapSize; i++) {
      let key = this.readString(streamin);
      let val = this.readString(streamin);
      map.set(key, val);
    }

    return map;
  }

  public writeBoolean(val: boolean, streamout: CubiqTS.ByteBuffer): void {
    streamout.writeIString(val ? "Y" : "N");
  }

  public writeDouble(val: CubiqTS.BigNumber, streamout: CubiqTS.ByteBuffer): void {
    streamout.writeIString(val.toString());
  }
  public writeByteBuffer(src: CubiqTS.ByteBuffer, streamout: CubiqTS.ByteBuffer): void {
    this.writeInt(src.limit, streamout);
    for (let i = 0; i < src.limit; ++i) streamout.writeByte(src.readByte());
  }
  public writeString(val: string, streamout: CubiqTS.ByteBuffer): void {
    streamout.writeIString(val);
  }

  public writeInt(val: number, streamout: CubiqTS.ByteBuffer): void {
    streamout.writeInt(val);
  }

  public writeLong(val: CubiqTS.Long, streamout: CubiqTS.ByteBuffer): void {
    streamout.writeInt64(val);
  }

  public writeMap(map: Map<string, string>, streamout: CubiqTS.ByteBuffer): void {
    this.writeInt(map == null ? 0 : map.size, streamout);

    if (map != null) {
      map.forEach((val: string, key: string) => {
        this.writeString(key, streamout);
        this.writeString(val, streamout);
      });
    }
  }

  public writeStartEncoding(stream: CubiqTS.ByteBuffer): void {
    let sFid: string = "^H";
    this.writeString(sFid, stream);
  }

  public writeEndEncoding(stream: CubiqTS.ByteBuffer): void {
    let sFid: string = "^F";
    this.writeString(sFid, stream);
  }

  public writeStartFid(fid: number, stream: CubiqTS.ByteBuffer): void {
    let sFid: string = "^X" + fid;
    this.writeString(sFid, stream);
  }

  public writeEndFid(fid: number, stream: CubiqTS.ByteBuffer): void {
    let sFid: string = "^Z" + fid;
    this.writeString(sFid, stream);
  }

  public parseCtrlAsFid(ctrl: string): number {
    return parseInt(ctrl.substring(2, ctrl.length));
  }

  public readCtrl(stream: CubiqTS.ByteBuffer): string {
    return this.readString(stream);
  }

  public readStartFid(stream: CubiqTS.ByteBuffer): number {
    let res: string = this.readString(stream);
    if (res.indexOf("^X") == -1) throw new Error("Invalid fid, missing control character");
    return parseInt(res.substring(2, res.length));
  }

  public readEndFid(stream: CubiqTS.ByteBuffer): number {
    let res: string = this.readString(stream);
    //if (res.indexOf("^Z") == -1)
    //throw new Error("Invalid end fid, missing control character " + res);
    //return parseInt(res.substring(2, res.length));
    return 0;
  }

  public seekToNextFid(fid: number, stream: CubiqTS.ByteBuffer): void {
    let notFound: boolean = true;
    let open: number = 0;
    while (notFound) {
      let check: string = this.readByte(stream);
      if (check == "^") {
        let control: string = this.readByte(stream);
        if (control == "Z") {
          if (open <= 0) {
            let len: number = fid.toString().length;
            while (len > 0) {
              this.readByte(stream);
              --len;
            }
            return;
          }
          --open;
        }
        if (control == "X") ++open;
      }
    }
  }

  public readDateFromString(stream: CubiqTS.ByteBuffer): CubiqTS.moment.Moment {
    let str: string = this.readString(stream);
    return CubiqTS.moment(new Date(str + "Z"));
  }

  public readObject(
    encodable: boolean,
    isArray: boolean,
    cls: string | undefined,
    stream: CubiqTS.ByteBuffer,
  ): any {
    if (encodable) {
      let clss: string = this.readString(stream);
      var objectTypeName: string | undefined =
        DefaultBaseObjectEncoderImpl.resolver.resolveObject(clss);

      if (!objectTypeName) {
        throw new Error("Failed to resolve objectTypeName: " + objectTypeName);
      }

      var objectInst: ConcreteEncodable = new CubiqTS[objectTypeName]();
      objectInst.setInnerEncoding(true);
      objectInst.encoder.decode(objectInst, stream);
      return objectInst;
    } else if (!cls) return this.readString(stream);
    else if (isArray) return this.readList(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isString(cls)) return this.readString(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isByteBuffer(cls))
      return this.readByteBuffer(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isBoolean(cls)) return this.readBoolean(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isDecimalNumber(cls))
      return this.readBigDecimal(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isDouble(cls)) return this.readDouble(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isLong(cls))
      return this.readLongFromString(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isInteger(cls))
      return this.readNumberFromString(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isDate(cls))
      return this.readDateFromString(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isMap(cls)) return this.readMap(stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isEnum(cls)) {
      // Keep inline with typescript internal semantics around enums
      let enumName: string = this.readString(stream);
      let enumTypename = DefaultBaseObjectEncoderImpl.resolver.resolveEnum(cls);
      if (!enumTypename) {
        throw new Error("Failed to resolve enum: " + enumTypename);
      }
      let enumType: string = CubiqTS[enumTypename];
      let enumValue: number = enumType[enumName];

      return enumValue;
    } else return this.readString(stream);
  }

  public writeDateAsString(val: CubiqTS.moment.Moment, stream: CubiqTS.ByteBuffer): void {
    val = val.isUTC() ? val : val.utc();
    return this.writeString(val.format(DefaultBaseObjectEncoderImpl.serverDateToken), stream);
  }

  public writeObject(
    isArray: boolean,
    type: string | undefined,
    encodable: any,
    o: any,
    stream: CubiqTS.ByteBuffer,
  ): void {
    if (encodable === true) {
      o.encoder.encode(o, stream);
    } else if (!type) this.writeString(<string>o, stream);
    else if (isArray) this.writeList(type, o, stream);
    else if (o instanceof Message) this.writeString(o.jsonMessage, stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isString(type))
      this.writeString(<string>o, stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isByteBuffer(type))
      this.writeByteBuffer(<CubiqTS.ByteBuffer>o, stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isDate(type))
      this.writeDateAsString(<CubiqTS.moment.Moment>o, stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isBoolean(type))
      this.writeBoolean(<boolean>o, stream);
    else if (
      DefaultBaseObjectEncoderImpl.resolver.isInteger(type) ||
      DefaultBaseObjectEncoderImpl.resolver.isDouble(type) ||
      DefaultBaseObjectEncoderImpl.resolver.isLong(type)
    )
      this.writeString(o == null ? null : o.toString(), stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isDecimalNumber(type)) {
      this.writeString(o == null ? null : o.toString(), stream);

      if (o != null) {
        this.writeInt(o.decimalPlaces(), stream);
      }
    } else if (DefaultBaseObjectEncoderImpl.resolver.isMap(type)) this.writeMap(o, stream);
    else if (DefaultBaseObjectEncoderImpl.resolver.isEnum(type)) {
      let enumValue: number = o as number;
      let enumTypename = DefaultBaseObjectEncoderImpl.resolver.resolveEnum(type);
      if (!enumTypename) {
        throw new Error("Failed to resolve enum: " + enumTypename);
      }
      let enumType: string = CubiqTS[enumTypename];
      let enumName: string = enumType[enumValue];
      this.writeString(enumName, stream);
    } else this.writeString(<string>o, stream);
  }

  public writeList(type: string, list: Array<any>, stream: CubiqTS.ByteBuffer): void {
    this.writeInt(list == null ? 0 : list.length, stream);
    if (list.length <= 0) return;
    if (list[0]?.encodableType !== undefined) type = list[0].encodableType;
    if (list != null && list.length > 0) this.writeString(type, stream);
    for (var i = 0; list != null && i < list.length; ++i)
      this.writeObject(false, type, this.isEncodableType(type), list[i], stream);
  }

  public isEncodableType(cls: string): boolean {
    return DefaultBaseObjectEncoderImpl.resolver.isEncodableType(cls);
  }

  public readList(stream: CubiqTS.ByteBuffer): Array<any> {
    let size: number = this.readInt(stream);
    if (size === 0) return [];
    if (size <= 0) {
      logger.out.info("DefaultBaseObjectEncoderImpl: readList() ignoring invalid size " + size);
      return [];
    }
    let list: Array<any> = [];
    let type: string = this.readString(stream);
    if (type == null || type === "") {
      logger.out.info(
        "DefaultBaseObjectEncoderImpl: readList() ignoring invalid type encoded " + type,
      );
      return [];
    }
    let failedCount: number = 0;
    for (var i = 0; i < size; ++i) {
      try {
        list.push(this.readObject(this.isEncodableType(type), false, type, stream));
      } catch (e) {
        failedCount++;
        logger.out.error(
          `DefaultBaseObjectEncoderImpl: readList item ${i + 1}/${size} type=${type} ` +
          `decode failed — ${list.length} items decoded so far. Error: ${e.message}`,
        );
        // For encodable types, try to scan past the corrupted item to the ^F end marker
        if (this.isEncodableType(type) && this.skipToEndOfEncodable(stream)) {
          logger.out.warn(
            `DefaultBaseObjectEncoderImpl: readList recovered stream at item ${i + 1}/${size} — continuing`,
          );
          continue;
        }
        // Stream unrecoverable for non-encodable types or if scan failed
        logger.out.error(
          `DefaultBaseObjectEncoderImpl: readList stream recovery failed at item ${i + 1}/${size}, ` +
          `returning ${list.length} of ${size} items`,
        );
        break;
      }
    }
    if (failedCount > 0) {
      logger.out.warn(
        `DefaultBaseObjectEncoderImpl: readList completed with ${list.length}/${size} items (${failedCount} failed)`,
      );
    }
    return list;
  }

  // Known Encodable namespace prefix: "com.bcdfx." as byte values
  private static readonly ENCODABLE_NS: number[] = [0x63, 0x6F, 0x6D, 0x2E, 0x62, 0x63, 0x64, 0x66, 0x78, 0x2E];

  private validateAfterEndMarker(stream: CubiqTS.ByteBuffer): boolean {
    let rem: number = stream.remaining();
    if (rem < 6) return true; // end of buffer

    let pos: number = stream.offset;
    let nextLen: number = ((stream.readUint8(pos) << 24)
        | (stream.readUint8(pos + 1) << 16)
        | (stream.readUint8(pos + 2) << 8)
        | stream.readUint8(pos + 3)) >>> 0;

    // Check: ^Z control marker — length 2-10, starts with ^Z (0x5E 0x5A)
    if (nextLen >= 2 && nextLen <= 10 && rem >= 6
        && stream.readUint8(pos + 4) === 0x5E
        && stream.readUint8(pos + 5) === 0x5A) {
      return true;
    }

    // Check: Encodable className — length 10-200, starts with "com.bcdfx."
    let ns = DefaultBaseObjectEncoderImpl.ENCODABLE_NS;
    if (nextLen >= ns.length && nextLen <= 200 && rem >= 4 + ns.length) {
      for (let i = 0; i < ns.length; i++) {
        if (stream.readUint8(pos + 4 + i) !== ns[i]) return false;
      }
      return true;
    }

    return false;
  }

  /**
   * Scan forward in the stream to find the end of the current encodable object.
   * Looks for the ^F end-of-encoding marker written as IString [00 00 00 02 5E 46],
   * tracking ^H/^F nesting depth to handle nested encodable objects.
   *
   * When a candidate ^F is found at depth=0, it is validated by peeking at the
   * bytes that follow. In a list of Encodables, a real ^F is always followed by:
   *   (a) next item's className IString starting with "com.bcdfx."
   *   (b) parent FID's ^Z end marker (0x5E 0x5A)
   *   (c) end of buffer
   * The 10-byte namespace check makes false positives effectively impossible.
   *
   * Returns true if the marker was found and stream position is advanced past it.
   */
  public skipToEndOfEncodable(stream: CubiqTS.ByteBuffer): boolean {
    let depth: number = 1;
    let scanned: number = 0;
    let limit: number = 4 * 1048576;

    while (scanned < limit && stream.remaining() >= 6) {
      let pos: number = stream.offset;
      let b0: number = stream.readUint8(pos);
      let b1: number = stream.readUint8(pos + 1);
      let b2: number = stream.readUint8(pos + 2);
      let b3: number = stream.readUint8(pos + 3);
      if (b0 === 0 && b1 === 0 && b2 === 0 && b3 === 2) {
        let c1: number = stream.readUint8(pos + 4);
        let c2: number = stream.readUint8(pos + 5);
        if (c1 === 0x5E) {
          if (c2 === 0x48) {
            depth++;
            stream.offset = pos + 6;
            scanned += 6;
            continue;
          }
          if (c2 === 0x46) {
            depth--;
            stream.offset = pos + 6;
            scanned += 6;
            if (depth <= 0) {
              if (this.validateAfterEndMarker(stream)) return true;
              depth++;
            }
            continue;
          }
        }
      }
      stream.offset = pos + 1;
      scanned++;
    }
    logger.out.error(
      `DefaultBaseObjectEncoderImpl: skipToEndOfEncodable exceeded scan limit or stream exhausted (scanned=${scanned})`,
    );
    return false;
  }
}
